import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Loading, Stack, TextInput } from '@carbon/react';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useOnboardingStatus } from 'src/hooks/useOnboardingStatus';
import { useMemberships } from 'src/hooks/useOrganizations';
import { orgRestaurantBranchCreate } from 'src/services/apis/tenantRequests';
import { deriveSlug } from 'src/utils/slug';
import StepLayout from './StepLayout';
import { branchSchema } from './schemas';

const BranchStep = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation('authentication');
  const { activeOrganizationId } = useAuthSession();
  const { data: status, isLoading: statusPending } = useOnboardingStatus();
  const { data: memberships, isLoading: membershipsPending } = useMemberships();
  const [error, setError] = useState<Error | null>(null);

  if (statusPending || membershipsPending) {
    return <Loading withOverlay description={t('onboarding.loading')} />;
  }

  const restaurantId = status?.firstRestaurantId;
  const active = memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0];

  async function onSubmit(values: { name: string; address: string }) {
    if (!restaurantId || !active) {
      setError(new Error(t('onboarding.branch.missingContext')));
      return;
    }
    try {
      await orgRestaurantBranchCreate(
        {
          name: values.name,
          address: values.address,
          slug: deriveSlug(values.name).slice(0, 22),
          restaurantId,
          photo: '',
          photoBlurhash: '',
          latitude: 0,
          longitude: 0,
          salesGoalPerDay: 0,
          salesGoalPerWeek: 0,
          salesGoalPerMonth: 0,
          salesGoalPerYear: 0,
          bankAccountName: '',
          bankAccountOwnerName: '',
          bankAccountNumber: '',
          bankAccountBranchName: '',
        },
        { orgSlug: active.slug, restaurantId }
      );
      onDone();
    } catch (e) {
      setError(e as Error);
    }
  }

  return (
    <Formik
      initialValues={{ name: '', address: '' }}
      validationSchema={toFormikValidationSchema(branchSchema(t))}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, isValid, dirty }) => (
        <StepLayout
          title={t('onboarding.branch.title')}
          subtitle={t('onboarding.branch.subtitle')}
          help={t('onboarding.branch.help')}
          error={error}
          onSubmit={handleSubmit}
          actions={
            <Button type="submit" disabled={isSubmitting || !isValid || !dirty || !restaurantId || !active}>
              {t('onboarding.continue')}
            </Button>
          }
        >
          <Stack gap={5}>
            <Field name="name">
              {({ field, meta }) => (
                <TextInput
                  id="branch-name"
                  labelText={t('onboarding.branch.name')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
            <Field name="address">
              {({ field, meta }) => (
                <TextInput
                  id="branch-address"
                  labelText={t('onboarding.branch.address')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
          </Stack>
        </StepLayout>
      )}
    </Formik>
  );
};

export default BranchStep;
